import {
  RadioCardGroup,
  type RadioCardOption,
} from '@/components/ui/radio-card-group';
import type { SeedStepProps } from '@/sections/seeds/components/modals/AddSeedByTypeModal/AddSeedByTypeModal.types';
import { useAuthenticationConfigurationAccess } from '@/sections/seeds/components/modals/AddSeedByTypeModal/hooks/useAuthenticationConfigurationAccess';
import { isWebSeed } from '@/sections/seeds/components/modals/AddSeedByTypeModal/wizardConfig';
import { SeedTypeOption } from '@/types';

// Canonical seed-line parser (order-preserving dedupe). MUST be the same
// implementation the submit hook uses, or credential assignment desyncs from
// seed creation. Here it pre-fills the COMPLEX_LOGIN URL from the first seed.
import { parseSeedLines } from './credentialAssign.util';
import { createInitialCredentialData } from './credentialStep.util';

const SKIP = 'skip';
const CONFIGURE = 'configure';
type SkipConfigureValue = typeof SKIP | typeof CONFIGURE;

function getSkipConfigureOptions(
  canConfigureAuthentication: boolean,
  disabledTooltip: string
): RadioCardOption<SkipConfigureValue>[] {
  return [
    {
      // Skip is the visually-secondary safe default — seed creation is never
      // blocked on authentication.
      value: SKIP,
      label: 'Skip Authentication',
      description: 'Add the seeds now; you can configure authentication later.',
    },
    {
      value: CONFIGURE,
      label: 'Configure Authentication',
      description:
        'Supply credentials so scans can reach pages behind a login.',
      disabled: !canConfigureAuthentication,
      disabledNote: !canConfigureAuthentication
        ? 'Requires Manage Integrations permission.'
        : undefined,
      disabledTooltip: !canConfigureAuthentication
        ? disabledTooltip
        : undefined,
    },
  ];
}

/**
 * CredentialStep — the wizard's authentication CHOICE step (Skip / Configure,
 * default Skip). Choosing "Configure" seeds the credential bundle into
 * `formData.credential.data` (fully controlled — security P2-2, so
 * `useWizard.reset()` clears every secret) and reveals the separate
 * CredentialConfigStep where the per-type form is filled in. This step always
 * validates to true so Next/Finish is never blocked. Keeping the choice on its
 * own screen — separate from the type picker + form — keeps each wizard step
 * small and digestible.
 */
export function CredentialStep({ formData, setFormData }: SeedStepProps) {
  const { canConfigureAuthentication, authenticationDisabledTooltip } =
    useAuthenticationConfigurationAccess();
  const options = getSkipConfigureOptions(
    canConfigureAuthentication,
    authenticationDisabledTooltip
  );

  const isWebService = formData.seedType === SeedTypeOption.WEB_SERVICE;
  const seedLines = parseSeedLines(formData.textInput);
  const firstSeedUrl = seedLines[0] ?? '';

  const handleModeChange = (next: SkipConfigureValue) => {
    if (next === SKIP) {
      setFormData(prev => ({ ...prev, credential: { mode: 'skip' } }));
      return;
    }
    if (!canConfigureAuthentication) return;

    // Multi-app path: ≥2 web seeds → configure-multi (per-app assignment).
    // The web-seed predicate MUST match wizardConfig's `isWebSeed` (used by the
    // CREDENTIAL_ASSIGN step's `shouldSkip`); a mismatch reintroduces dead UI.
    const isMulti = isWebSeed(formData) && seedLines.length > 1;
    setFormData(prev => {
      if (isMulti) {
        // Preserve an in-progress multi bundle if the user toggles Skip↔Configure.
        if (prev.credential.mode === 'configure-multi') return prev;
        return {
          ...prev,
          credential: {
            mode: 'configure-multi',
            shared: null,
            perApp: {},
            custom: {},
          },
        };
      }
      // Single-seed path (unchanged): preserve an in-progress configure bundle.
      if (prev.credential.mode === 'configure') return prev;
      return {
        ...prev,
        credential: {
          mode: 'configure',
          data: createInitialCredentialData(isWebService, firstSeedUrl),
        },
      };
    });
  };

  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-6">
      <div className="flex flex-col gap-1.5 text-center">
        <h3 className="text-chariot-text-primary text-base font-semibold">
          Do you want to authenticate?
        </h3>
        <p className="text-chariot-text-secondary text-sm">
          Choose whether to supply credentials now or add them later.
        </p>
      </div>
      <div className="w-full max-w-md">
        <RadioCardGroup
          value={formData.credential.mode === 'skip' ? SKIP : CONFIGURE}
          onChange={handleModeChange}
          options={options}
          ariaLabel="Authentication choice"
        />
      </div>
    </div>
  );
}
